import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import { 
    PencilSquareIcon, 
    CheckIcon, 
    XMarkIcon, 
    ArrowPathIcon, 
    PaintBrushIcon,
    ArrowUpTrayIcon,
    ArrowDownTrayIcon,
    CheckCircleIcon,
    ArrowsRightLeftIcon,
    UsersIcon,
    ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';
import { calculateLevel, calculate8Level, calculateMeanScore, getLevelData } from '../../utils/cbcUtils';
import TermSelector from '../common/TermSelector';
import ConfirmModal from '../common/ConfirmModal';
import Modal from '../common/Modal';
import { useQuery } from '@tanstack/react-query';

const GradeBook = ({ courseId, clubId, selectedTerm: externalTerm, onTermChange: externalOnTermChange, onNavigateTab, onBandCountsChange }) => {
    const { get, put, post, patch, del } = useApi();
    const { showToast, performanceBands } = useAppState();
    const navigate = useNavigate();
    const autoRefreshRef = useRef(null);
    const [gradebook, setGradebook] = useState({ students: [], assignments: [] });
    const [loading, setLoading] = useState(true);       // initial mount only
    const [refreshing, setRefreshing] = useState(false); // background/manual syncs
    const [editingCell, setEditingCell] = useState(null); // {studentId, assignmentId}
    const [editValue, setEditValue] = useState('');
    const [editingStrandCell, setEditingStrandCell] = useState(null); // {studentId, assignmentId, strandId}
    const [editStrandValue, setEditStrandValue] = useState('');
    const [localSaving, setLocalSaving] = useState(false);
    const [internalTerm, setInternalTerm] = useState(externalTerm || '');
    const selectedTerm = externalTerm !== undefined && externalTerm !== '' ? externalTerm : internalTerm;
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // {studentId, assignmentId, gradeId}
    const [importing, setImporting] = useState(false);
    const [importSummary, setImportSummary] = useState(null);
    const [activeExpandedAssignmentId, setActiveExpandedAssignmentId] = useState(null);
    const fileInputRef = useRef(null);

    const handleTermChange = useCallback((newTerm) => {
        setInternalTerm(newTerm);
        if (externalOnTermChange) {
            externalOnTermChange(newTerm);
        }
    }, [externalOnTermChange]);

    const handleNavigateToAssessments = () => {
        if (onNavigateTab) {
            onNavigateTab('assignments');
        }
        window.dispatchEvent(new CustomEvent('change-course-tab', { detail: 'assignments' }));
        const url = new URL(window.location);
        url.searchParams.set('tab', 'assignments');
        window.history.pushState({}, '', url);
    };

    const toggleExpandAssignment = (assignmentId) => {
        setActiveExpandedAssignmentId(prev => (prev === assignmentId ? null : assignmentId));
    };

    const fetchGradebook = useCallback(async (isInitial = false) => {
        if (!courseId && !clubId) return;
        try {
            if (isInitial) setLoading(true);
            else setRefreshing(true);
            
            const termParam = selectedTerm ? `?term_id=${selectedTerm}` : '';
            const endpoint = clubId
                ? `/api/events/club-gradebook/${clubId}/${termParam}`
                : `/courses/api/${courseId}/gradebook/${termParam}`;
                
            const data = await get(endpoint);
            setGradebook(data);
        } catch (error) {
            console.error('Error fetching gradebook:', error);
            showToast('Failed to load gradebook', 'error');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [courseId, clubId, get, showToast, selectedTerm]);

    useEffect(() => {
        if (courseId || clubId) {
            fetchGradebook(true); // initial mount — shows spinner
        }
    }, [courseId, clubId, fetchGradebook]);

    // Fetch terms
    const { data: termsData } = useQuery({
        queryKey: ['academic-terms'],
        queryFn: () => get('/api/academic-terms/'),
        staleTime: 60 * 60 * 1000,
    });

    const terms = useMemo(() => termsData || [], [termsData]);

    // Set default term if not set
    useEffect(() => {
        if (terms.length > 0 && !selectedTerm) {
            const currentTerm = terms.find(t => t.is_current);
            const termId = currentTerm ? currentTerm.id.toString() : terms[0].id.toString();
            handleTermChange(termId);
        }
    }, [terms, selectedTerm, handleTermChange]);

    // Tally EE, ME, AE, BE distribution for parent components (e.g. TeacherCourseView header metric cards)
    useEffect(() => {
        if (!onBandCountsChange || !gradebook?.students) return;
        const counts = { EE: 0, ME: 0, AE: 0, BE: 0 };
        (gradebook.students || []).forEach(student => {
            const percentages = [];
            (gradebook.assignments || []).forEach(a => {
                const cell = student.grades ? student.grades[a.id] : null;
                const grade = cell?.grade_data || cell;
                if (grade && grade.score !== null && grade.score !== undefined) {
                    percentages.push((parseFloat(grade.score) / parseFloat(a.total_marks || 20)) * 100);
                }
            });
            if (student.quiz_grades) {
                Object.values(student.quiz_grades).forEach(qg => {
                    if (qg && qg.total > 0) {
                        percentages.push((parseFloat(qg.score) / parseFloat(qg.total)) * 100);
                    }
                });
            }
            if (percentages.length > 0) {
                const avg = percentages.reduce((a, b) => a + b, 0) / percentages.length;
                const band = (performanceBands && performanceBands.length > 0)
                    ? calculate8Level(avg, performanceBands).substring(0, 2)
                    : (avg >= 70 ? 'EE' : avg >= 50 ? 'ME' : avg >= 30 ? 'AE' : 'BE');
                if (counts[band] !== undefined) {
                    counts[band]++;
                }
            }
        });
        onBandCountsChange(counts);
    }, [gradebook, performanceBands, onBandCountsChange]);

    // Auto-refresh every 60 seconds — background, never shows full spinner
    useEffect(() => {
        if (!courseId && !clubId) return;
        autoRefreshRef.current = setInterval(() => {
            fetchGradebook(false);
        }, 60000);
        return () => clearInterval(autoRefreshRef.current);
    }, [courseId, clubId, fetchGradebook]);

    const handleEditClick = (studentId, assignmentId, currentScore) => {
        setActiveExpandedAssignmentId(assignmentId);
        setEditingCell({ studentId, assignmentId });
        setEditValue(currentScore !== null && currentScore !== undefined ? Math.round(parseFloat(currentScore)).toString() : '');
    };

    const handleSaveGrade = async (studentId, assignmentId) => {
        const student = gradebook.students.find(s => s.student_id === studentId);
        const cellData = student?.grades[assignmentId];
        const gradeData = cellData?.grade_data || cellData;
        const assignment = gradebook.assignments.find(a => a.id === assignmentId);

        const score = parseFloat(editValue);
        if (isNaN(score) || score < 0) {
            showToast('Please enter a valid score', 'error');
            return;
        }

        // VALIDATION: Score cannot exceed total marks
        if (assignment && assignment.total_marks && score > parseFloat(assignment.total_marks)) {
            showToast(`Score cannot exceed the total marks (${assignment.total_marks})`, 'error');
            return;
        }

        try {
            setLocalSaving(true);
            const gradeEndpoint = clubId ? '/api/events/club-grades/' : '/courses/api/grades/';
            const newLevel = calculateLevel(score, assignment.total_marks, performanceBands);
            let savedGradeId = gradeData?.grade_id;

            if (gradeData && gradeData.grade_id) {
                // Update existing grade
                await put(`${gradeEndpoint}${gradeData.grade_id}/`, {
                    score: score,
                    competency_level: newLevel
                });
            } else {
                // Create new grade
                const payload = clubId ? {
                    submission: cellData?.submission_id,
                    score: score,
                    competency_level: newLevel
                } : {
                    student: studentId,
                    assignment: assignmentId,
                    score: score,
                    competency_level: newLevel
                };
                const response = await post(gradeEndpoint, payload);
                savedGradeId = response?.grade_id || response?.id;
            }

            // ✅ Optimistic in-memory update — no table reload
            setGradebook(prev => ({
                ...prev,
                students: prev.students.map(s => {
                    if ((s.student_id ?? s.id) !== studentId) return s;
                    return {
                        ...s,
                        grades: {
                            ...s.grades,
                            [String(assignmentId)]: {
                                score,
                                competency_level: newLevel,
                                letter_grade: '',
                                grade_id: savedGradeId,
                                graded_via_marking_page: false
                            }
                        }
                    };
                })
            }));

            showToast('Grade saved successfully');
            setEditingCell(null);
        } catch (error) {
            console.error('Error saving grade:', error);
            if (error.response && error.response.data && typeof error.response.data === 'string' && error.response.data.includes('<html')) {
                const html = error.response.data;
                const typeMatch = html.match(/<th>Exception Type:<\/th>\s*<td>(.*?)<\/td>/);
                const valMatch = html.match(/<th>Exception Value:<\/th>\s*<td><pre>(.*?)<\/pre><\/td>/);
                
                console.error("================ DJANGO 500 ERROR ================");
                if (typeMatch) console.error("Type:", typeMatch[1].trim());
                if (valMatch) console.error("Value:", valMatch[1].trim());
                
                // Fallback to print start of HTML if regex fails
                if (!typeMatch && !valMatch) {
                    console.error("HTML Snippet:", html.substring(0, 1000));
                }
                console.error("==================================================");
            }
            showToast('Failed to save grade', 'error');
        } finally {
            setLocalSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingCell(null);
        setEditValue('');
    };
    
    const handleSaveStrandGrade = async (studentId, assignmentId, strandId, strandMax) => {
        const student = gradebook.students.find(s => (s.student_id ?? s.id) === studentId);
        const cellData = student?.grades[assignmentId];
        const gradeData = cellData?.grade_data || cellData;
        const assignment = gradebook.assignments.find(a => a.id === assignmentId);

        const val = parseFloat(editStrandValue);
        if (isNaN(val) || val < 0) {
            showToast('Please enter a valid strand mark', 'error');
            return;
        }
        if (val > strandMax) {
            showToast(`Strand mark cannot exceed ${strandMax}`, 'error');
            return;
        }

        try {
            setLocalSaving(true);
            const submissionId = gradeData?.submission_id;

            let existingScores = {};
            if (gradeData && gradeData.marking_data && gradeData.marking_data.strand_scores) {
                existingScores = { ...gradeData.marking_data.strand_scores };
            } else if (assignment && assignment.tested_strands_detail) {
                const totalMarks = parseFloat(assignment.total_marks) || 100;
                const currentScore = gradeData && gradeData.score !== null ? parseFloat(gradeData.score) : 0;
                const pct = totalMarks > 0 ? (currentScore / totalMarks) : 0;

                assignment.tested_strands_detail.forEach(st => {
                    const threshObj = (assignment.strand_thresholds || {})[st.id] || {};
                    const sMax = Math.round(parseFloat(threshObj.max) || (totalMarks / assignment.tested_strands_detail.length));
                    existingScores[st.id] = Math.round(pct * sMax);
                });
            }

            existingScores[strandId] = Math.round(val);
            const newTotalScore = Object.values(existingScores).reduce((a, b) => a + parseFloat(b || 0), 0);
            const newLevel = calculateLevel(newTotalScore, assignment.total_marks, performanceBands);

            const payload = {
                grade: newTotalScore,
                score: newTotalScore,
                competency_level: newLevel,
                status: 'graded',
                marking_data: {
                    ...(gradeData?.marking_data || {}),
                    strand_scores: existingScores
                }
            };

            let saveRes = null;
            if (submissionId) {
                saveRes = await patch(`/courses/assignment-submissions/${submissionId}/`, payload);
            } else {
                const createPayload = {
                    student: studentId,
                    assignment: assignmentId,
                    grade: newTotalScore,
                    score: newTotalScore,
                    competency_level: newLevel,
                    status: 'graded',
                    marking_data: { strand_scores: existingScores }
                };
                saveRes = await post('/courses/assignment-submissions/', createPayload);
            }

            const returnedSubId = saveRes?.id || submissionId;

            // Optimistic in-memory update
            setGradebook(prev => ({
                ...prev,
                students: prev.students.map(s => {
                    if ((s.student_id ?? s.id) !== studentId) return s;
                    return {
                        ...s,
                        grades: {
                            ...s.grades,
                            [String(assignmentId)]: {
                                ...(s.grades[String(assignmentId)] || {}),
                                score: newTotalScore,
                                competency_level: newLevel,
                                submission_id: returnedSubId,
                                grade_id: returnedSubId,
                                marking_data: {
                                    ...(gradeData?.marking_data || {}),
                                    strand_scores: existingScores
                                }
                            }
                        }
                    };
                })
            }));

            showToast('Strand grade saved successfully');
            setEditingStrandCell(null);
        } catch (err) {
            console.error('Error saving strand grade:', err);
            showToast('Failed to save strand grade', 'error');
        } finally {
            setLocalSaving(false);
        }
    };
    
    const handleRemoveGrade = async () => {
        if (!showDeleteConfirm) return;
        const { studentId, assignmentId, gradeId } = showDeleteConfirm;
        const student = gradebook.students.find(s => (s.student_id ?? s.id) === studentId);
        const cellData = student?.grades[assignmentId];
        const gradeData = cellData?.grade_data || cellData;

        try {
            setLocalSaving(true);
            const submissionId = gradeData?.submission_id;

            if (submissionId) {
                await patch(`/courses/assignment-submissions/${submissionId}/`, {
                    grade: null,
                    score: null,
                    competency_level: null,
                    status: 'unsubmitted',
                    marking_data: {}
                });
            } else if (gradeId) {
                const gradeEndpoint = clubId ? '/api/events/club-grades/' : '/courses/api/grades/';
                await del(`${gradeEndpoint}${gradeId}/`);
            }

            // Optimistic update
            setGradebook(prev => ({
                ...prev,
                students: prev.students.map(s => {
                    if ((s.student_id ?? s.id) !== studentId) return s;
                    const newGrades = { ...s.grades };
                    newGrades[String(assignmentId)] = null;
                    return { ...s, grades: newGrades };
                })
            }));

            showToast('Grade removed successfully', 'success');
            setShowDeleteConfirm(null);
            setEditingCell(null);
            setEditingStrandCell(null);
        } catch (error) {
            console.error('Error removing grade:', error);
            showToast('Failed to remove grade', 'error');
        } finally {
            setLocalSaving(false);
        }
    };

    const handleExport = async () => {
        try {
            showToast('Preparing gradebook excel...', 'info');
            const endpoint = `/courses/api/${courseId}/export-gradebook/?term_id=${selectedTerm}`;
            
            // Use get from useApi but with blob response type
            const blob = await get(endpoint, { responseType: 'blob' });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            
            // Generate filename based on metadata or fallback
            const metadata = gradebook.metadata || {};
            const subjectName = metadata.subject_name || 'Gradebook';
            const gradeLevel = metadata.grade_level || '';
            const termName = metadata.term || `Term${selectedTerm}`;
            const timestamp = new Date().toISOString().split('T')[0];
            
            const levelPart = gradeLevel ? `Grade_${gradeLevel.replace(/\s+/g, '_')}` : '';
            const finalFilename = `${subjectName.replace(/\s+/g, '_')}${levelPart ? '_' + levelPart : ''}_${termName.replace(/\s+/g, '_')}_${timestamp}.xlsx`;
            
            link.setAttribute('download', finalFilename);
            
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            showToast('Download started', 'success');
        } catch (error) {
            console.error('Export error:', error);
            let message = 'Failed to export gradebook';
            const data = error?.response?.data;
            if (data instanceof Blob && data.type && data.type.includes('json')) {
                try {
                    const parsed = JSON.parse(await data.text());
                    message = parsed.error || parsed.detail || message;
                } catch (parseErr) {
                    // keep generic message if the error body isn't parseable JSON
                }
            }
            showToast(message, 'error');
        }
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            setImporting(true);
            showToast('Uploading and processing excel...', 'info');
            
            const endpoint = `/courses/api/${courseId}/import-gradebook/`;
            const result = await post(endpoint, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (result.success) {
                setImportSummary(result); // Store full result (summary + error_details)
                fetchGradebook(false); // Refresh data
            } else {
                showToast(result.error || 'Failed to import grades', 'error');
            }
        } catch (error) {
            console.error('Import error:', error);
            showToast(error.message || 'Error during import', 'error');
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };


    const hasAssignments = gradebook?.assignments && gradebook.assignments.length > 0;
    const hasQuizzes = gradebook?.quizzes && gradebook.quizzes.length > 0;
    const hasAssessments = hasAssignments || hasQuizzes;
    const hasStudents = gradebook?.students && gradebook.students.length > 0;

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
                <div>
                    <h2 className="text-lg font-black text-[#18216D] uppercase tracking-tight">Rubric Grading Ledger</h2>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Competency-Based Assessment</p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="hidden md:flex items-center gap-2 border-r border-slate-100 pr-4">
                        <button
                            onClick={handleExport}
                            disabled={!hasAssessments || !hasStudents}
                            className="flex items-center gap-2 px-3 py-2 text-[11px] font-black uppercase tracking-widest text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Download Excel Template"
                        >
                            <ArrowDownTrayIcon className="w-4 h-4" />
                            <span>Export</span>
                        </button>
                        <button
                            onClick={handleImportClick}
                            disabled={importing || !hasAssessments}
                            className="flex items-center gap-2 px-3 py-2 text-[11px] font-black uppercase tracking-widest text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Upload Completed Excel"
                        >
                            <ArrowUpTrayIcon className="w-4 h-4" />
                            <span>{importing ? 'Importing...' : 'Import'}</span>
                        </button>
                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            onChange={handleFileChange} 
                            className="hidden" 
                            accept=".xlsx, .xls"
                        />
                    </div>
                    <TermSelector 
                        terms={terms} 
                        selectedTerm={selectedTerm} 
                        onTermChange={handleTermChange} 
                        isLoading={refreshing || loading}
                    />
                    <button
                        onClick={() => fetchGradebook(false)}
                        disabled={refreshing || loading}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all active:rotate-180 duration-500 disabled:opacity-40"
                        title="Sync Data"
                    >
                        <ArrowPathIcon className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20 bg-white rounded-[2.5rem] border border-slate-100 shadow-sm">
                    <div className="flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-600 border-t-transparent"></div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Loading Grading Ledger...</p>
                    </div>
                </div>
            ) : !hasStudents ? (
                <div className="text-center py-20 bg-white rounded-[2.5rem] border-2 border-dashed border-slate-100 shadow-sm">
                    <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-slate-400">
                        <UsersIcon className="w-8 h-8" />
                    </div>
                    <h3 className="text-base font-black text-[#18216D]">No Students Enrolled</h3>
                    <p className="text-xs font-bold text-slate-400 mt-1 max-w-sm mx-auto">
                        No students are currently enrolled in this learning area.
                    </p>
                </div>
            ) : !hasAssessments ? (
                <div className="text-center py-20 bg-white rounded-[2.5rem] border-2 border-dashed border-slate-100 shadow-sm">
                    <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-amber-500">
                        <ClipboardDocumentCheckIcon className="w-8 h-8" />
                    </div>
                    <h3 className="text-base font-black text-[#18216D]">No Assessments for this Term</h3>
                    <p className="text-xs font-bold text-slate-400 mt-1 max-w-md mx-auto leading-relaxed">
                        No assignments or quizzes were found for the selected academic term. Switch terms using the selector above or{' '}
                        <button
                            type="button"
                            onClick={handleNavigateToAssessments}
                            className="text-[#18216D] hover:text-indigo-600 underline font-black transition-colors inline cursor-pointer"
                        >
                            create an assessment
                        </button>{' '}
                        in the Assessments tab.
                    </p>
                </div>
            ) : (

            <div className="w-full bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                                Student
                            </th>
                            {(() => {
                                const displayedAssignments = activeExpandedAssignmentId 
                                    ? gradebook.assignments.filter(a => String(a.id) === String(activeExpandedAssignmentId))
                                    : gradebook.assignments;
                                return displayedAssignments.map((assignment) => {
                                    const isExpanded = String(activeExpandedAssignmentId) === String(assignment.id);
                                    const hasStrands = assignment.tested_strands_detail && assignment.tested_strands_detail.length > 0;

                                    return (
                                        <React.Fragment key={`asgn-${assignment.id}`}>
                                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[170px] bg-slate-50/50">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="flex flex-col text-left">
                                                        <span className="font-bold text-gray-900">{assignment.title}</span>
                                                        <span className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-widest">
                                                            {assignment.total_marks} Marks
                                                        </span>
                                                    </div>
                                                    {hasStrands && (
                                                        <button
                                                            onClick={() => toggleExpandAssignment(assignment.id)}
                                                            className={`p-1.5 rounded-lg border transition-all ${
                                                                isExpanded
                                                                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                                                    : 'bg-white text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border-slate-200'
                                                            }`}
                                                            title={isExpanded ? "Collapse Tested Strands" : "Expand Tested Strands"}
                                                        >
                                                            <ArrowsRightLeftIcon className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </th>
                                            {isExpanded && hasStrands && assignment.tested_strands_detail.map((strand) => (
                                                <th key={`asgn-${assignment.id}-strand-${strand.id}`} className="px-4 py-3 text-center text-xs font-medium text-indigo-700 bg-indigo-50/60 uppercase tracking-wider min-w-[170px] border-l border-indigo-100">
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-indigo-900 text-[11px] truncate max-w-[150px]">{strand.name}</span>
                                                        <span className="text-[9px] text-indigo-500 font-bold uppercase tracking-widest mt-0.5">Strand</span>
                                                    </div>
                                                </th>
                                            ))}
                                        </React.Fragment>
                                    );
                                });
                            })()}
                            {!activeExpandedAssignmentId && gradebook.quizzes && gradebook.quizzes.map((quiz) => (
                                <th key={`quiz-${quiz.id}`} className="px-6 py-3 text-center text-xs font-medium text-[#18216D]/60 uppercase tracking-wider min-w-[140px] bg-amber-50/30">
                                    <div className="flex flex-col">
                                        <span className="font-black text-[#18216D]">{quiz.title}</span>
                                        <span className="text-[10px] text-amber-600/60 mt-1 font-black uppercase tracking-widest">
                                            Auto-Quiz ({quiz.total_points} pts)
                                        </span>
                                    </div>
                                </th>
                            ))}
                            {!activeExpandedAssignmentId && (
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">
                                    Average
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {gradebook.students.map((student) => {
                            // Unified Average Calculation: Assignments + Quizzes as Percentages
                            const percentages = [];

                            gradebook.assignments.forEach(a => {
                                const cell = student.grades[a.id];
                                const grade = cell?.grade_data || cell; // Support both structures
                                if (grade && grade.score !== null && grade.score !== undefined) {
                                    percentages.push((parseFloat(grade.score) / parseFloat(a.total_marks || 20)) * 100);
                                }
                            });

                            if (student.quiz_grades) {
                                Object.values(student.quiz_grades).forEach(qg => {
                                    if (qg && qg.total > 0) {
                                        percentages.push((parseFloat(qg.score) / parseFloat(qg.total)) * 100);
                                    }
                                });
                            }

                            const average = percentages.length > 0
                                ? (percentages.reduce((a, b) => a + b, 0) / percentages.length).toFixed(1)
                                : '-';

                            const displayedAssignments = activeExpandedAssignmentId 
                                ? gradebook.assignments.filter(a => String(a.id) === String(activeExpandedAssignmentId))
                                : gradebook.assignments;

                            return (
                                <tr key={student.student_id || student.id} className="hover:bg-gray-50">
                                    <td className="sticky left-0 z-10 bg-white px-6 py-4 whitespace-nowrap border-r border-gray-200 min-w-[180px]">
                                        <div className="flex flex-col">
                                            <div className="text-sm font-medium text-gray-900">
                                                {student.student_name || student.name}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {student.student_number || student.student_id}
                                            </div>
                                        </div>
                                    </td>
                                    {displayedAssignments.map((assignment) => {
                                        const cell = student.grades[assignment.id];
                                        const gradeData = cell?.grade_data || cell;
                                        const isEditing = editingCell?.studentId === (student.student_id || student.id) &&
                                            editingCell?.assignmentId === assignment.id;
                                        const isExpanded = String(activeExpandedAssignmentId) === String(assignment.id);
                                        const hasStrands = assignment.tested_strands_detail && assignment.tested_strands_detail.length > 0;

                                        return (
                                            <React.Fragment key={`asgn-${assignment.id}`}>
                                                <td className="px-6 py-4 whitespace-nowrap text-center border-r border-gray-50 min-w-[170px]">
                                                    {isEditing ? (
                                                        <div className="flex items-center space-x-2 p-1.5 bg-blue-50/70 rounded-xl border border-blue-200 shadow-sm animate-in fade-in zoom-in duration-200">
                                                            <div className="relative flex items-center">
                                                                <input
                                                                    type="number"
                                                                    value={editValue}
                                                                    onChange={(e) => setEditValue(e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.ctrlKey && (e.key === 'Delete' || e.key === 'Backspace')) {
                                                                            e.preventDefault();
                                                                            if (gradeData?.grade_id || gradeData?.submission_id) {
                                                                                setShowDeleteConfirm({ 
                                                                                    studentId: student.student_id || student.id, 
                                                                                    assignmentId: assignment.id, 
                                                                                    gradeId: gradeData?.grade_id 
                                                                                });
                                                                            } else {
                                                                                handleCancelEdit();
                                                                            }
                                                                        } else if (e.key === 'Enter') {
                                                                            handleSaveGrade(student.student_id || student.id, assignment.id);
                                                                        } else if (e.key === 'Escape') {
                                                                            handleCancelEdit();
                                                                        }
                                                                    }}
                                                                    className="w-16 px-2 py-1.5 text-sm font-black text-[#18216D] border-2 border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-right"
                                                                    autoFocus
                                                                    min="0"
                                                                    max={assignment.total_marks}
                                                                    step="1"
                                                                />
                                                                <span className="ml-2 text-xs font-bold text-slate-400">
                                                                    / {Math.round(assignment.total_marks)}
                                                                </span>
                                                            </div>
                                                            <div className="flex space-x-1 ml-2 py-1 px-1 bg-white rounded-lg border border-slate-200">
                                                                <button
                                                                    onClick={() => handleSaveGrade(student.student_id || student.id, assignment.id)}
                                                                    disabled={localSaving}
                                                                    className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors disabled:opacity-50"
                                                                    title="Save Grade"
                                                                >
                                                                    {localSaving ? (
                                                                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                                                    ) : (
                                                                        <CheckIcon className="w-4 h-4" />
                                                                    )}
                                                                </button>
                                                                <button
                                                                    onClick={handleCancelEdit}
                                                                    className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                                                                    title="Cancel"
                                                                >
                                                                    <XMarkIcon className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        (() => {
                                                            const isMarkedViaCanvas = gradeData?.graded_via_marking_page;
                                                            return (
                                                                <div
                                                                    tabIndex={0}
                                                                    className={`group inline-flex flex-col items-center space-y-1 px-3 py-1 rounded transition-colors outline-none focus:ring-2 focus:ring-indigo-300 ${isMarkedViaCanvas ? 'cursor-default hover:bg-indigo-50/40' : 'cursor-pointer hover:bg-slate-50'}`}
                                                                    onClick={() => {
                                                                        if (isMarkedViaCanvas) return;
                                                                        handleEditClick(student.student_id || student.id, assignment.id, gradeData?.score);
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.ctrlKey && (e.key === 'Delete' || e.key === 'Backspace')) {
                                                                            e.preventDefault();
                                                                            if (gradeData?.grade_id || gradeData?.submission_id) {
                                                                                setShowDeleteConfirm({ 
                                                                                    studentId: student.student_id || student.id, 
                                                                                    assignmentId: assignment.id, 
                                                                                    gradeId: gradeData?.grade_id 
                                                                                });
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    {gradeData && gradeData.score !== null && gradeData.score !== undefined ? (
                                                                        <>
                                                                            <span className="text-base font-black text-[#18216D]">
                                                                                {Math.round((parseFloat(gradeData.score) / parseFloat(assignment.total_marks || 20)) * 100)}%
                                                                            </span>
                                                                            {(() => {
                                                                                const level = gradeData.competency_level || calculateLevel(gradeData.score, assignment.total_marks, performanceBands);
                                                                                const data = getLevelData(level);
                                                                                return (
                                                                                    <span className={`text-[11px] font-black px-3 py-1 rounded-lg uppercase tracking-widest border-2 shadow-sm hover:shadow-md transition-all ${data.bg} ${data.color} ${data.border}`}>
                                                                                        {level}
                                                                                    </span>
                                                                                );
                                                                            })()}
                                                                        </>
                                                                    ) : (
                                                                        <span className="text-sm text-slate-300 font-bold">-</span>
                                                                    )}
                                                                    {isMarkedViaCanvas ? (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                navigate(`/teacher/assignments/${assignment.id}/mark/${gradeData.submission_id}`);
                                                                            }}
                                                                            className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-lg"
                                                                            title="Graded via Marking Canvas"
                                                                        >
                                                                            <PaintBrushIcon className="w-3 h-3 text-indigo-400" />
                                                                            <span className="text-[8px] font-black uppercase tracking-widest text-indigo-400">Marked</span>
                                                                        </button>
                                                                    ) : (
                                                                        <PencilSquareIcon className="w-4 h-4 text-[#18216D] opacity-0 group-hover:opacity-100 transition-all duration-200" />
                                                                    )}
                                                                </div>
                                                            );
                                                        })()
                                                    )}
                                                </td>
                                                {isExpanded && hasStrands && assignment.tested_strands_detail.map((strand) => {
                                                    const totalMarks = parseFloat(assignment?.total_marks) || 20;
                                                    const isStrandEditing = editingStrandCell?.studentId === (student.student_id || student.id) &&
                                                        editingStrandCell?.assignmentId === assignment.id &&
                                                        editingStrandCell?.strandId === strand.id;

                                                    const strandThresholdObj = (assignment?.strand_thresholds || {})[strand.id] || {};
                                                    const strandMax = Math.round(parseFloat(strandThresholdObj.max) || (totalMarks / assignment.tested_strands_detail.length));

                                                    const explicitScores = gradeData?.marking_data?.strand_scores || {};
                                                    let strandScore = null;

                                                    if (explicitScores[strand.id] !== undefined && explicitScores[strand.id] !== null) {
                                                        strandScore = Math.round(parseFloat(explicitScores[strand.id]));
                                                    } else if (explicitScores[String(strand.id)] !== undefined && explicitScores[String(strand.id)] !== null) {
                                                        strandScore = Math.round(parseFloat(explicitScores[String(strand.id)]));
                                                    } else if (gradeData && gradeData.score !== null && gradeData.score !== undefined) {
                                                        const pct = parseFloat(gradeData.score) / totalMarks;
                                                        strandScore = Math.round(pct * strandMax);
                                                    }

                                                    const strandPct = strandScore !== null && strandMax > 0 ? Math.round((strandScore / strandMax) * 100) : null;
                                                    const strandLevel = strandPct !== null ? calculateLevel(strandPct, 100, performanceBands) : null;
                                                    const levelData = strandLevel ? getLevelData(strandLevel) : null;

                                                    return (
                                                        <td key={`asgn-${assignment.id}-strand-${strand.id}`} className="px-4 py-4 whitespace-nowrap text-center bg-indigo-50/20 border-l border-indigo-100 min-w-[170px]">
                                                            {isStrandEditing ? (
                                                                <div className="flex items-center space-x-1.5 p-1 bg-white rounded-xl border border-indigo-200 shadow-sm animate-in fade-in zoom-in duration-200">
                                                                    <input
                                                                        type="number"
                                                                        value={editStrandValue}
                                                                        onChange={(e) => setEditStrandValue(e.target.value)}
                                                                        onKeyDown={(e) => {
                                                                            if (e.ctrlKey && (e.key === 'Delete' || e.key === 'Backspace')) {
                                                                                e.preventDefault();
                                                                                if (gradeData?.grade_id || gradeData?.submission_id) {
                                                                                    setShowDeleteConfirm({ 
                                                                                        studentId: student.student_id || student.id, 
                                                                                        assignmentId: assignment.id, 
                                                                                        gradeId: gradeData?.grade_id 
                                                                                    });
                                                                                } else {
                                                                                    setEditingStrandCell(null);
                                                                                }
                                                                            } else if (e.key === 'Enter') {
                                                                                handleSaveStrandGrade(student.student_id || student.id, assignment.id, strand.id, strandMax);
                                                                            } else if (e.key === 'Escape') {
                                                                                setEditingStrandCell(null);
                                                                            }
                                                                        }}
                                                                        className="w-14 px-2 py-1 text-xs font-black text-[#18216D] border border-indigo-200 rounded bg-indigo-50/30 text-right outline-none focus:ring-2 focus:ring-indigo-500"
                                                                        autoFocus
                                                                        min="0"
                                                                        max={strandMax}
                                                                        step="1"
                                                                    />
                                                                    <span className="text-[10px] font-bold text-slate-400">/ {strandMax}</span>
                                                                    <button
                                                                        onClick={() => handleSaveStrandGrade(student.student_id || student.id, assignment.id, strand.id, strandMax)}
                                                                        disabled={localSaving}
                                                                        className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors disabled:opacity-50"
                                                                        title="Save Strand Mark"
                                                                    >
                                                                        {localSaving ? (
                                                                            <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                                                                        ) : (
                                                                            <CheckIcon className="w-3.5 h-3.5" />
                                                                        )}
                                                                    </button>
                                                                    <button
                                                                        onClick={() => setEditingStrandCell(null)}
                                                                        className="p-1 text-rose-600 hover:bg-rose-50 rounded transition-colors"
                                                                        title="Cancel"
                                                                    >
                                                                        <XMarkIcon className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div
                                                                    tabIndex={0}
                                                                    className="group inline-flex flex-col items-center space-y-1 px-3 py-1 rounded transition-colors cursor-pointer hover:bg-slate-50 outline-none focus:ring-2 focus:ring-indigo-300"
                                                                    onClick={() => {
                                                                        setEditingStrandCell({ studentId: student.student_id || student.id, assignmentId: assignment.id, strandId: strand.id });
                                                                        setEditStrandValue(strandScore !== null ? strandScore.toString() : '');
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.ctrlKey && (e.key === 'Delete' || e.key === 'Backspace')) {
                                                                            e.preventDefault();
                                                                            if (gradeData?.grade_id || gradeData?.submission_id) {
                                                                                setShowDeleteConfirm({ 
                                                                                    studentId: student.student_id || student.id, 
                                                                                    assignmentId: assignment.id, 
                                                                                    gradeId: gradeData?.grade_id 
                                                                                });
                                                                            }
                                                                        }
                                                                    }}
                                                                    title="Click to edit strand mark"
                                                                >
                                                                    {strandScore !== null ? (
                                                                        <>
                                                                            <span className="text-base font-black text-[#18216D]">
                                                                                {strandScore} / {strandMax}
                                                                            </span>
                                                                            {levelData && (
                                                                                <span className={`text-[11px] font-black px-3 py-1 rounded-lg uppercase tracking-widest border-2 shadow-sm hover:shadow-md transition-all ${levelData.bg} ${levelData.color} ${levelData.border}`}>
                                                                                    {strandLevel}
                                                                                </span>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <span className="text-sm text-slate-300 font-bold">-</span>
                                                                    )}
                                                                    <PencilSquareIcon className="w-4 h-4 text-[#18216D] opacity-0 group-hover:opacity-100 transition-all duration-200" />
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                    {!activeExpandedAssignmentId && gradebook.quizzes && gradebook.quizzes.map((quiz) => {
                                        const quizGrade = student.quiz_grades ? student.quiz_grades[quiz.id] : null;
                                        // Prioritize backend-calculated levels for academic consistency
                                        const levelCode = quizGrade?.competency_level || (quizGrade ? calculateLevel(quizGrade.score, quizGrade.total, performanceBands) : null);
                                        const levelData = levelCode ? getLevelData(levelCode) : null;

                                        return (
                                            <td key={`quiz-${quiz.id}`} className="px-6 py-4 whitespace-nowrap text-center bg-amber-50/5 border-r border-amber-100/20">
                                                {quizGrade ? (
                                                    <div className="flex flex-col items-center">
                                                        <span className={`text-[11px] font-black px-2.5 py-1 rounded-lg uppercase tracking-widest border-2 shadow-sm hover:shadow-md transition-all ${levelData?.bg} ${levelData?.color} ${levelData?.border}`}>
                                                            {levelCode}
                                                        </span>
                                                        <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest mt-1.5 opacity-60">
                                                            {quizGrade.score} / {quizGrade.total}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-[9px] text-slate-300 font-bold uppercase tracking-widest">Unattempted</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                    {!activeExpandedAssignmentId && (
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <div className="flex flex-col items-center gap-1">
                                                <span className="text-sm font-black text-[#18216D]">
                                                    {average !== '-' ? `${average}%` : '-'}
                                                </span>
                                                {average !== '-' && (
                                                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter">
                                                        {calculate8Level(average, performanceBands)} • {calculateMeanScore(average, performanceBands).toFixed(2)} MS
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            )}

            <div className="bg-indigo-50/60 border border-indigo-100 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm">
                <div className="flex items-center gap-2.5">
                    <span className="h-2 w-2 rounded-full bg-indigo-600 flex-shrink-0"></span>
                    <p className="text-xs font-bold text-slate-700">
                        <strong className="text-[#18216D]">Tip:</strong> Click on any grade cell to edit.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="bg-white border border-indigo-100 text-[#18216D] px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider shadow-sm">
                        Ctrl + Delete to delete grade
                    </span>
                </div>
            </div>

            <ConfirmModal
                isOpen={!!showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(null)}
                onConfirm={handleRemoveGrade}
                loading={localSaving}
                title="Remove Student Grade?"
                message="This will completely remove the grade for this student. They will appear as 'Ungraded' in the system. Proceed?"
                confirmText="Yes, Remove Grade"
                variant="danger"
            />

            {/* Import Success Modal */}
            <Modal
                isOpen={!!importSummary}
                onClose={() => setImportSummary(null)}
                title="Import Successful"
                footer={
                    <button
                        onClick={() => setImportSummary(null)}
                        className="px-10 py-4 bg-[#18216D] text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-[#0D164F] transition-all shadow-xl shadow-indigo-900/20"
                    >
                        Done
                    </button>
                }
            >
                <div className="space-y-6">
                    <div className="flex items-center gap-4 p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
                        <div className="h-12 w-12 bg-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-200">
                            <CheckCircleIcon className="h-7 w-7 text-white" />
                        </div>
                        <div>
                            <h4 className="text-sm font-black text-emerald-900 uppercase tracking-tight">Grades Updated</h4>
                            <p className="text-xs font-bold text-emerald-600/80">Batch processing completed successfully</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        {[
                            { label: 'Exceeding (EE)', key: 'EE', color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-100' },
                            { label: 'Meeting (ME)', key: 'ME', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
                            { label: 'Approaching (AE)', key: 'AE', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' },
                            { label: 'Below (BE)', key: 'BE', color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-100' }
                        ].map((item) => (
                            <div key={item.key} className={`${item.bg} ${item.border} border p-4 rounded-2xl transition-all hover:scale-[1.02]`}>
                                <div className="flex justify-between items-start mb-1">
                                    <span className={`text-[9px] font-black uppercase tracking-widest ${item.color} opacity-70`}>{item.label}</span>
                                    <div className={`h-1.5 w-1.5 rounded-full ${item.color.replace('text', 'bg')}`}></div>
                                </div>
                                <div className={`text-2xl font-black ${item.color}`}>
                                    {importSummary ? (importSummary.summary ? importSummary.summary[item.key] : importSummary[item.key]) || 0 : 0}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 text-[10px] font-bold text-slate-500 leading-relaxed uppercase tracking-widest text-center">
                        Registry synchronization complete for all students
                    </div>

                    {/* Flagged Issues Section */}
                    {importSummary?.error_details?.length > 0 && (
                        <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="flex items-center gap-2 px-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse"></div>
                                <h5 className="text-[10px] font-black text-rose-600 uppercase tracking-widest">Flagged Issues ({importSummary.errors})</h5>
                            </div>
                            <div className="max-h-32 overflow-y-auto pr-2 custom-scrollbar">
                                <div className="space-y-2">
                                    {importSummary.error_details.map((err, idx) => (
                                        <div key={idx} className="p-3 bg-rose-50/50 border border-rose-100/50 rounded-xl flex items-start gap-3">
                                            <div className="mt-0.5 text-rose-400">
                                                <XMarkIcon className="h-3 w-3" />
                                            </div>
                                            <p className="text-[10px] font-bold text-rose-700/80 leading-relaxed">
                                                {err}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>

        </div>
    );
};

export default GradeBook;
